import React, { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { useAuthModalContext } from '../../context/AuthModalContext';
import { AuthModal } from '../AuthModal';
import { Header } from '../Header';

const AUTH_PATHS = ['/login', '/signup'];

/**
 * Navbar - Self-contained navigation bar with integrated auth modal
 *
 * Usage: Simply include <Navbar /> in your page layout.
 * Requires AuthModalProvider in the component tree.
 *
 * /login and /signup are real URLs: visiting them directly (or without
 * JavaScript, via the prerendered pages) shows the auth UI; clicking the
 * header links with JavaScript on pushes the URL and opens the modal in
 * place. Closing the modal restores the underlying route's URL.
 */
export const Navbar: React.FC<{ authIsLoading?: boolean; centerContent?: React.ReactNode }> = ({ authIsLoading = false, centerContent }) => {
  const { isOpen, openModal, closeModal } = useAuthModalContext();
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // direct load / SPA navigation onto an auth URL opens the modal — but only
  // for anonymous users; a logged-in user landing on /login is sent away
  // instead of being shown the login card again
  useEffect(() => {
    if (!AUTH_PATHS.includes(location.pathname) || isLoading) return;

    if (isAuthenticated) {
      navigate('/', { replace: true });

      return;
    }

    openModal();
  }, [location.pathname, isAuthenticated, isLoading, navigate, openModal]);

  const handleClose = useCallback(() => {
    closeModal();
    if (AUTH_PATHS.includes(window.location.pathname)) {
      if (AUTH_PATHS.includes(location.pathname)) {
        // routed here directly — leave the auth page
        navigate('/', { replace: true });
      } else {
        // URL was pushed over another page — restore that page's URL
        window.history.replaceState({}, '', location.pathname + location.search);
      }
    }
  }, [closeModal, location.pathname, location.search, navigate]);

  return (
    <>
      <Header onLoginClick={openModal} onSignUpClick={openModal} authIsLoading={authIsLoading} centerContent={centerContent} />
      <AuthModal isOpen={isOpen} onClose={handleClose} />
    </>
  );
};
